/**
 * What changed inside a block, as field ids.
 *
 * An undo used to pulse the whole block, which says "something here" and nothing more. Add alt text
 * to an image, delete another block, undo twice, and the image block flashes with no indication
 * that alt text is what came back — and if the change was in feedback while the editor is open,
 * nothing visible happens at all.
 *
 * The op records whole-data snapshots (`prev`, `next`) because `handleUpdateBlock` receives the
 * whole data object and nothing narrower, so the field has to be recovered by comparing them.
 *
 * Three kinds of answer, matching the three shapes block data actually takes:
 *
 *   - **an item's id**, for arrays of ided objects — options, items, answers, blanks, series,
 *     branches, and `feedback.states`. These already carry `data-field-id` and are what a teacher
 *     edits most often.
 *   - **a key**, for a top-level scalar — `altText`, `chartType`. The field id simply IS the key,
 *     which makes scalars simpler to tag than arrays rather than harder.
 *   - **a dotted path**, for a scalar nested inside a plain object — `handleB.min`. Qualified
 *     because `min` alone would be ambiguous with the block's own `min`.
 *
 * Ids are returned flat and unqualified because that is what `field-reveal` already assumes: option
 * and series ids are unique across a task, so one registry can serve every carousel without a
 * block-id namespace.
 */

type Data = Record<string, unknown>;

const isPlainObject = (v: unknown): v is Data =>
  !!v && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);

/** An array of objects that all carry a string `id` — the shape whose items are addressable. */
function idedItems(v: unknown): Data[] | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  return v.every(x => isPlainObject(x) && typeof x.id === 'string') ? (v as Data[]) : null;
}

/**
 * Content, with key order taken out of it.
 *
 * `same` was a bare `JSON.stringify` comparison, which is key-order SENSITIVE — and an object
 * rebuilt by a spread comes out in a different order than it went in. Clearing one option's image
 * rewrote that option through `{ ...o, media, formula, ...}` and left every other option untouched
 * BY REFERENCE, but the push that followed serialised the list afresh: an option that had been
 * `id,text,role,anchored,media` came back as `id,text,media,role,anchored`, identical in content
 * and unequal by string.
 *
 * So an untouched item was reported as changed. It named no key beneath it — the per-key loop
 * found nothing to disagree about — just its own bare id, which is how an undo came to ring the
 * row below the one that changed: with the real row mid-rerender, the ladder walked past it to
 * the next id in the list, and that id should never have been in the list.
 *
 * Arrays are NOT sorted. Reordering a list is a real change — dragging an option into a new
 * position is exactly the edit that reports as one — so only the keys of an object are normalised.
 */
function stableShape(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stableShape);
  if (isPlainObject(v)) {
    const out: Data = {};
    for (const key of Object.keys(v).sort()) out[key] = stableShape(v[key]);
    return out;
  }
  return v;
}

const same = (a: unknown, b: unknown) =>
  JSON.stringify(stableShape(a)) === JSON.stringify(stableShape(b));

/**
 * Diff two lists by id.
 *
 * An item added or removed is reported as much as one edited — restoring a deleted option should
 * point at the option, not shrug because it is absent from one side.
 */
function diffItems(prev: Data[], next: Data[]): string[] {
  const before = new Map(prev.map(x => [x.id as string, x]));
  const after = new Map(next.map(x => [x.id as string, x]));
  const out: string[] = [];
  for (const [id, item] of after) {
    const was = before.get(id);
    if (!was) { out.push(id); continue; }   // appeared: nothing finer is the point
    if (same(was, item)) continue;
    /**
     * An EDITED item names each changed key beneath it before naming itself.
     *
     * The item id alone is too coarse the moment an item has more than one editable part. A
     * feedback state holds a message, a trigger and an evaluation; an answer holds a value and its
     * tolerances. Undoing an edit to a feedback message used to highlight the whole card — and a
     * collapsed card shows only its title, so it said nothing at all about what had changed.
     *
     * The bare id stays, last, as the fallback for anything not tagged that finely.
     */
    for (const key of new Set([...Object.keys(was), ...Object.keys(item)])) {
      if (key !== 'id' && !same(was[key], item[key])) out.push(`${id}.${key}`);
    }
    out.push(id);
  }
  for (const id of before.keys()) if (!after.has(id)) out.push(id);   // vanished: likewise
  return out;
}

function walk(prev: Data, next: Data, path: string, out: string[]): void {
  for (const key of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    const a = prev[key];
    const b = next[key];
    if (same(a, b)) continue;

    const items = idedItems(b) ?? idedItems(a);
    if (items) {
      out.push(...diffItems(idedItems(a) ?? [], idedItems(b) ?? []));
      /**
       * A list that appeared or vanished ALSO names its key, after its items.
       *
       * Six blocks put conditional correctness behind a toggle whose only trace in the data is
       * whether `conditionalBranches` exists. Reporting only the branch ids means that turning it
       * off points at rows that have just stopped rendering — nothing is found, and the pulse
       * falls back to flashing the whole block. The key is what the TOGGLE is tagged with, so it
       * is the one thing still on screen.
       *
       * Last, not first: ids are tried in order, so a restored branch still pulses the branch.
       */
      if (!idedItems(a) || !idedItems(b)) out.push(path ? `${path}.${key}` : key);
      continue;
    }
    if (isPlainObject(a) && isPlainObject(b)) {
      walk(a, b, path ? `${path}.${key}` : key, out);
      continue;
    }
    // A scalar, an array of scalars, or a shape that changed kind: the field is the key itself.
    out.push(path ? `${path}.${key}` : key);
  }
}

/**
 * The keys whose change means the block's whole answer surface was REPLACED.
 *
 * Linking to a scenario, unlinking, migrating a bank into rows, adding or removing a version —
 * all of them rewrite the question, the answer key and the feedback together, because that is
 * what they are for.
 */
const STRUCTURAL_KEYS = ['versions', 'scenarioBranches', 'followsBlockId'];

export function changedFields(prev: Data | undefined, next: Data | undefined): string[] {
  if (!prev || !next) return [];
  const out: string[] = [];
  walk(prev, next, '', out);
  // De-duplicated: two keys under one object can name the same item id.
  const all = [...new Set(out)];

  /*
   * A STRUCTURAL CHANGE NAMES ITSELF, and nothing else.
   *
   * Undoing a migration listed the question, every answer, the feedback states and the rows —
   * all true, all changed, and together meaningless as a place to point. The ring walks the
   * list opening whatever surface each id lives on, so it opened the feedback panel to reveal
   * a state, then jumped back to the editor when a higher-priority id turned up: reported as
   * the undo "switching to feedback briefly".
   *
   * There is no single field to ring here, and pretending otherwise is what caused the
   * thrash. Naming the structural key alone leaves the ring nothing to reveal — no control
   * carries `data-field-id="versions"` — so it falls back to the block, which is the honest
   * answer for a change that replaced the block.
   */
  const structural = all.filter(f => STRUCTURAL_KEYS.includes(f));
  return structural.length > 0 ? structural : all;
}
